import React from 'react'

import styled, { css } from 'styled-components'

export interface PrimaryRadioWithOverlayProps {
  checked: boolean
  disabled?: boolean
  className?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  id: string
  name: string
  children?: React.ReactNode
  label?: React.ReactNode
}

const HiddenRadio = styled.input.attrs({ type: 'radio' })<{
  disabled?: boolean
}>`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  cursor: pointer;
  z-index: -1;
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`

const Container = styled.label<{ checked: boolean; disabled?: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  border-radius: 16px;
  border: 1px solid
    ${({ checked, theme }) =>
      checked ? theme.color.brandGreen : theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }
  &:focus-within {
    background: ${({ theme }) => theme.color.hoverWhite};
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
  }
  &:active {
    background: ${({ theme }) => theme.color.white};
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
  }
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.3;
    `}
`

const Visual = styled.div<{ checked: boolean; disabled?: boolean }>`
  height: 32px;
  width: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease-in-out;
  background-color: transparent;
  pointer-events: none;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    border-radius: 50%;
    top: 0;
    left: 0;
    width: 32px;
    height: 32px;

    ${({ checked, disabled, theme }) =>
      !disabled &&
      css`
        ${Container}:hover & {
          background-color: ${checked
            ? theme.color.lightGreen
            : theme.color.lightGrey};
        }
        ${Container}:active & {
          background-color: unset;
        }
      `}
    ${({ disabled }) =>
      disabled &&
      css`
        opacity: 0.5;
      `}
  }
`

const Ring = styled.div<{ checked: boolean }>`
  z-index: 1;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ checked, theme }) =>
    checked ? theme.color.lightGreen : 'transparent'};
  border: 1px solid
    ${({ checked, theme }) =>
      checked ? theme.color.brandGreen : theme.color.darkGrey};
  transition: border-color 150ms ease-in-out;

  ${({ theme, checked }) => css`
    ${Container}:focus-within & {
      border-color: ${theme.color.brandGreen};
      background-color: ${theme.color.white};
    }
    ${Container}:hover & {
      background-color: ${checked
        ? theme.color.lightGreen
        : theme.color.lightGrey};
    }
    ${Container}:active & {
      background-color: ${checked && theme.color.lightGrey};
      border-color: ${theme.color.slateGrey};
    }
  `}
`

const Dot = styled.div<{ checked: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transition: all 150ms ease-in-out;
  background-color: ${({ checked, theme }) =>
    checked ? theme.color.brandGreen : 'transparent'};
  transform: scale(${({ checked }) => (checked ? 1 : 0)});

  ${({ theme }) => css`
    ${Container}:active & {
      background-color: ${theme.color.slateGrey};
    }
  `}
`
const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
`
export const PrimaryRadioWithOverlay: React.FC<
  PrimaryRadioWithOverlayProps
> = ({
  checked,
  disabled = false,
  onChange,
  id,
  name,
  className,
  children,
  label,
}) => (
  <Container
    disabled={disabled}
    checked={checked}
    className={className}
    htmlFor={id}
  >
    <LabelWrapper>
      <HiddenRadio
        id={id}
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <Visual checked={checked} disabled={disabled}>
        <Ring checked={checked}>
          <Dot checked={checked} />
        </Ring>
      </Visual>
      {label}
    </LabelWrapper>
    {children}
  </Container>
)
